import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Insured } from 'src/types';
import { ENTITY_KEY, INSURED_QUERY_KEY, PUT_KEY } from '../queryKeys';
import { queryYemboApiCall, queryYemboApiCallWithReturnValue } from '../queryYemboApiCalls';

const url = 'insured';

type PartialInsured = Partial<Insured> & { key: string };

type UseInsuredOptions = UseQueryOptions<Insured, unknown>;

/** `GET /insured/:key` answers the insured itself; so do the writes below. */
const getInsured = async (insuredKey: string): Promise<Insured> => {
  const insured = await queryYemboApiCallWithReturnValue<Insured>({
    url: `${url}/${insuredKey}`,
    shouldNotifyAndThrowError: true,
  });

  if (!insured) throw new Error('Insured not found');

  return insured;
};

const putInsured = async (insured: PartialInsured) => {
  const response = await queryYemboApiCall({
    url: `${url}/${insured.key}`,
    method: 'PUT',
    body: JSON.stringify(insured),
    logoutIfNotAuthorized: false,
    shouldNotifyAndThrowError: true,
  });

  return (await response.json()) as Insured;
};

export const postInsured = async (insured: Insured & { shouldNotify: boolean }) => {
  const response = await queryYemboApiCall({
    url,
    method: 'POST',
    body: JSON.stringify(insured),
    logoutIfNotAuthorized: false,
    shouldNotifyAndThrowError: true,
  });

  return (await response.json()) as Insured;
};

export const useInsuredQuery = (insuredKey: string | undefined, options?: Partial<UseInsuredOptions>) =>
  useQuery({
    ...options,
    queryKey: [INSURED_QUERY_KEY, ENTITY_KEY, insuredKey],
    queryFn: () => getInsured(insuredKey ?? ''),
    enabled: !!insuredKey && options?.enabled !== false,
  });

type PutInsuredOptions = UseMutationOptions<Insured, unknown, PartialInsured>;

export const usePutInsured = (options?: PutInsuredOptions) => {
  const mutationKey = [INSURED_QUERY_KEY, PUT_KEY];
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey,
    mutationFn: putInsured,
    ...options,
    onMutate: async (insured, context) => {
      const queryKey = [INSURED_QUERY_KEY, ENTITY_KEY, insured.key];
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData<Insured>(
        queryKey,
        (currentInsured) => currentInsured && { ...currentInsured, ...insured }
      );
      options?.onMutate?.(insured, context);
    },
    onSettled: (response, error, insured, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: [INSURED_QUERY_KEY, ENTITY_KEY, insured.key],
      });
      options?.onSettled?.(response, error, insured, onMutateResult, context);
    },
  });
};

export const putInsuredCheckDuplicates = async (data: { phone?: string; email?: string; companyKey: string }) => {
  const response = await queryYemboApiCall({
    url: 'insured/check-duplicates',
    method: 'PUT',
    body: JSON.stringify(data),
    shouldNotifyAndThrowError: true,
  });

  return (await response.json()) as
    | {
        hasPotentialDuplicate: false;
      }
    | {
        hasPotentialDuplicate: true;
        insured: Insured;
        jobKey: string;
      };
};
